import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'

import Keycloak from 'keycloak-js'

import { useToast } from '@pnld/components-web'

import {
  getAuthVerify,
  postAuthRefresh,
  postAuthLogout,
  postAuthKeycloak,
} from '@/api/auth/services'
import { KeycloakConfig } from '@/api/auth/types'
import { ROUTES } from '@/config/routes'
import { getErrorMessage, isThrottledError } from '@/utils/api'
import { stringToBoolean } from '@/utils/string'

interface AuthContextType {
  isAuthenticated: boolean

  isLoading: boolean

  error: string | null

  loginWithKeycloak: () => void

  logout: () => Promise<void>

  setAuthenticated: (value: boolean) => void

  checkAuth: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | null>(null)

const REFRESH_INTERVAL = 10 * 60 * 1000

const keycloakConfig: KeycloakConfig = {
  url: process.env.REACT_APP_KEYCLOAK_URL || '',
  realm: process.env.REACT_APP_KEYCLOAK_REALM || 'GOVBR',
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || '',
  scope: 'openid profile email address',
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const keycloakRef = useRef<Keycloak | null>(null)
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const initRef = useRef(false)

  const navigate = useNavigate()
  const toast = useToast()

  const clearRefreshInterval = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current)
      refreshIntervalRef.current = null
    }
  }, [])

  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    try {
      await postAuthRefresh()
      return true
    } catch (error) {
      // 429 não é sessão inválida: mantém o login e o próximo ciclo tenta de novo.
      if (isThrottledError(error)) {
        return false
      }
      clearRefreshInterval()
      setIsAuthenticated(false)
      return false
    }
  }, [clearRefreshInterval])

  const startRefreshInterval = useCallback(() => {
    clearRefreshInterval()
    refreshIntervalRef.current = setInterval(
      refreshAccessToken,
      REFRESH_INTERVAL
    )
  }, [clearRefreshInterval, refreshAccessToken])

  const checkAuth = useCallback(async (): Promise<boolean> => {
    try {
      const response = await getAuthVerify()
      const authenticated = response.authenticated

      setIsAuthenticated(authenticated)
      setError(null)

      if (authenticated) {
        startRefreshInterval()
      } else {
        clearRefreshInterval()
      }

      return authenticated
    } catch {
      setIsAuthenticated(false)
      clearRefreshInterval()
      return false
    }
  }, [startRefreshInterval, clearRefreshInterval])

  const setAuthenticated = useCallback(
    (value: boolean) => {
      setIsAuthenticated(value)
      setIsLoading(false)
      setError(null)
      if (value) {
        startRefreshInterval()
      } else {
        clearRefreshInterval()
      }
    },
    [startRefreshInterval, clearRefreshInterval]
  )

  const loginWithKeycloak = useCallback(() => {
    if (!keycloakRef.current) {
      setError('Sistema de autenticação não inicializado')
      return
    }

    keycloakRef.current.login({
      prompt: 'login',
      redirectUri: window.location.origin + ROUTES.LOGIN,
    })
  }, [])

  const logout = useCallback(async () => {
    try {
      await postAuthLogout()
    } catch {
      // Ignore errors - we'll redirect anyway
    }

    clearRefreshInterval()
    setIsAuthenticated(false)
    setError(null)

    if (keycloakRef.current?.authenticated) {
      keycloakRef.current.clearToken()
      keycloakRef.current.logout({
        redirectUri: window.location.origin + ROUTES.LOGIN,
      })
      return
    }

    navigate(ROUTES.LOGIN, { replace: true })
  }, [navigate, clearRefreshInterval])

  const processOAuthCallback = useCallback(
    async (kc: Keycloak) => {
      const token = kc.token as string

      try {
        await postAuthKeycloak({ token })

        setIsAuthenticated(true)
        setIsLoading(false)
        setError(null)
        startRefreshInterval()

        navigate(ROUTES.DEFAULT_AUTHENTICATED, { replace: true })
      } catch (err: unknown) {
        const error = err as { response?: { status?: number } }
        if (error.response?.status === 401) {
          setError('Usuário não cadastrado no sistema')
          navigate(ROUTES.ACCESS_DENIED, { replace: true })
        } else {
          // Nenhuma tela lê `error`: sem o toast, a falha (ex.: 429) passava calada.
          const message = getErrorMessage(err, 'Erro na validação do usuário.')
          setError(message)
          toast({
            status: 'error',
            title: 'Ops!',
            description: message,
            isClosable: true,
          })
        }

        setIsAuthenticated(false)
        setIsLoading(false)
      }
    },
    [navigate, startRefreshInterval, toast]
  )

  useEffect(() => {
    if (initRef.current) return
    initRef.current = true

    if (stringToBoolean(process.env.REACT_APP_DISABLE_AUTH)) {
      setIsAuthenticated(true)
      setIsLoading(false)
      return
    }

    const initialize = async () => {
      try {
        const kc = new Keycloak(keycloakConfig)

        await kc.init({
          checkLoginIframe: false,
          pkceMethod: 'S256',
          enableLogging: process.env.NODE_ENV === 'development',
        })

        keycloakRef.current = kc

        if (kc.authenticated && kc.token) {
          await processOAuthCallback(kc)
          return
        }

        await checkAuth()
        setIsLoading(false)
      } catch (err) {
        console.error('Auth initialization error:', err)
        setError('Erro ao inicializar autenticação')
        setIsLoading(false)
      }
    }

    initialize()

    return () => {
      clearRefreshInterval()
    }
  }, [checkAuth, clearRefreshInterval, processOAuthCallback])

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      error,
      loginWithKeycloak,
      logout,
      setAuthenticated,
      checkAuth,
    }),
    [
      isAuthenticated,
      isLoading,
      error,
      loginWithKeycloak,
      logout,
      setAuthenticated,
      checkAuth,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider')
  }
  return context
}
